import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const { t, language } = useLanguage();

  // Add welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: t('chat.welcome')
      }]);
    }
  }, [isOpen, messages.length, t]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage,
        language: language
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: language === 'es' 
          ? 'Lo siento, hubo un error. Por favor intenta de nuevo o contáctanos directamente.'
          : 'Sorry, there was an error. Please try again or contact us directly.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: 'spring' }}
        onClick={() => setIsOpen(true)}
        data-testid="chatbot-toggle-btn"
        className={`fixed bottom-24 right-6 z-[60] w-14 h-14 rounded-full bg-gradient-to-r from-[var(--primary-main)] to-[var(--primary-light)] flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 ${isOpen ? 'hidden' : ''}`}
      >
        <MessageCircle className="w-6 h-6 text-white" />
        {/* Pulse animation */}
        <span className="absolute w-full h-full rounded-full bg-[var(--primary-main)] animate-ping opacity-30" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            data-testid="chatbot-window"
            className="fixed bottom-24 right-6 z-[60] w-[90vw] max-w-[380px] h-[500px] max-h-[70vh] rounded-2xl glass-strong overflow-hidden flex flex-col shadow-2xl border border-[var(--border-highlight)]"
          >
            {/* Header */}
            <div className="p-4 border-b border-[var(--border-subtle)] flex items-center justify-between bg-gradient-to-r from-[var(--primary-dark)] to-[var(--primary-main)]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--accent-gold)] bg-opacity-20 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-[var(--accent-gold)]" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-white">{t('chat.title')}</h3>
                  <span className="text-xs text-[var(--text-secondary)]">Powered by AI</span>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                data-testid="chatbot-close-btn"
                className="p-2 rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chatbot-messages">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' 
                      ? 'bg-[var(--accent-gold)] bg-opacity-20' 
                      : 'bg-[var(--primary-main)] bg-opacity-20'
                  }`}>
                    {msg.role === 'user' 
                      ? <User className="w-4 h-4 text-[var(--accent-gold)]" />
                      : <Bot className="w-4 h-4 text-[var(--primary-light)]" />
                    }
                  </div>
                  <div className={`max-w-[75%] p-3 rounded-2xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-[var(--primary-main)] text-white rounded-br-none'
                      : 'bg-[var(--bg-subtle)] text-[var(--text-primary)] rounded-bl-none border border-[var(--border-subtle)]'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--primary-main)] bg-opacity-20 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-[var(--primary-light)]" />
                  </div>
                  <div className="bg-[var(--bg-subtle)] p-3 rounded-2xl rounded-bl-none border border-[var(--border-subtle)]">
                    <Loader2 className="w-5 h-5 text-[var(--primary-light)] animate-spin" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-[var(--border-subtle)] bg-[var(--bg-paper)]">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('chat.placeholder')}
                  data-testid="chatbot-input"
                  className="flex-1 bg-[var(--bg-subtle)] border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--primary-main)] transition-colors"
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !input.trim()}
                  data-testid="chatbot-send-btn"
                  className="w-12 h-12 rounded-xl bg-gradient-to-r from-[var(--primary-main)] to-[var(--primary-light)] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg"
                >
                  <Send className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
